import React, { useEffect, useState } from 'react';

interface ScenarioCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (scenarioData: ScenarioFormData) => void;
  existingScenarios: { id: string; name: string }[];
  editingScenario?: ScenarioFormData & { id: string };
  onUpdate?: (scenarioId: string, scenarioData: ScenarioFormData) => void;
  cloningScenario?: { id: string; name: string };
  onClone?: (sourceScenarioId: string, scenarioData: ScenarioFormData) => void;
}

interface ScenarioFormData {
  name: string;
  headlinePrice: number;
  businessName: string;
  bankAmount: number;
  bankRateAnnual: number;
  bankTermYears: number;
  sellerAmount: number;
  sellerRateAnnual: number;
  sellerStructure: 'amort' | 'io' | 'io_then_amort';
  sellerAmortizationYears?: number;
  sellerBalloonYears: number;
  sellerBalloonDueMonth: number;
  sellerIoMonths?: number;
  annualSde?: number;
  monthlySde?: number;
  financialMetricType?: string;
  annualThreshold?: number;
  quarterlyThreshold?: number;
  cloneFromId?: string | null;
}

export function ScenarioCreateModal({ isOpen, onClose, onCreate, existingScenarios, editingScenario, onUpdate, cloningScenario, onClone }: ScenarioCreateModalProps) {
  const [formData, setFormData] = useState<ScenarioFormData>({
    name: '',
    headlinePrice: 1000000,
    businessName: 'Advanced HVAC',
    bankAmount: 500000,
    bankRateAnnual: 6.5,
    bankTermYears: 10,
    sellerAmount: 500000,
    sellerRateAnnual: 8.0,
    sellerStructure: 'amort',
    sellerAmortizationYears: 5,
    sellerBalloonYears: 5,
    sellerBalloonDueMonth: 12,
    sellerIoMonths: undefined,
    annualSde: 300000,
    monthlySde: 25000,
    financialMetricType: 'EBITDA',
    annualThreshold: 325000,
    quarterlyThreshold: 81250,
    cloneFromId: null
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      if (editingScenario) {
        // Populate form with existing scenario data
        setFormData({
          name: editingScenario.name || '',
          headlinePrice: editingScenario.headlinePrice || 1000000,
          businessName: editingScenario.businessName || 'Advanced HVAC',
          bankAmount: editingScenario.bankAmount || 500000,
          bankRateAnnual: editingScenario.bankRateAnnual || 6.5,
          bankTermYears: editingScenario.bankTermYears || 10,
          sellerAmount: editingScenario.sellerAmount || 500000,
          sellerRateAnnual: editingScenario.sellerRateAnnual || 8.0,
          sellerStructure: editingScenario.sellerStructure || 'amort',
          sellerAmortizationYears: editingScenario.sellerAmortizationYears || 5,
          sellerBalloonYears: editingScenario.sellerBalloonYears || 5,
          sellerBalloonDueMonth: editingScenario.sellerBalloonDueMonth || 12,
          sellerIoMonths: editingScenario.sellerIoMonths || undefined,
          annualSde: editingScenario.annualSde || 300000,
          monthlySde: editingScenario.monthlySde || 25000,
          financialMetricType: editingScenario.financialMetricType || 'EBITDA',
          annualThreshold: editingScenario.annualThreshold || 325000,
          quarterlyThreshold: editingScenario.quarterlyThreshold || 81250,
          cloneFromId: null
        });
      } else if (cloningScenario) {
        // For cloning, we'll need to load the source scenario data
        // For now, set a default name and let the user fill in the rest
        setFormData({
          name: `${cloningScenario.name} (Copy)`,
          headlinePrice: 1000000,
          businessName: 'Advanced HVAC',
          bankAmount: 500000,
          bankRateAnnual: 6.5,
          bankTermYears: 10,
          sellerAmount: 500000,
          sellerRateAnnual: 8.0,
          sellerStructure: 'amort',
          sellerAmortizationYears: 5,
          sellerBalloonYears: 5,
          sellerBalloonDueMonth: 12,
          sellerIoMonths: undefined,
          annualSde: 300000,
          monthlySde: 25000,
          financialMetricType: 'EBITDA',
          annualThreshold: 325000,
          quarterlyThreshold: 81250,
          cloneFromId: cloningScenario.id
        });
      } else {
        // Reset to default values for new scenario
        setFormData({
          name: '',
          headlinePrice: 1000000,
          businessName: 'Advanced HVAC',
          bankAmount: 500000,
          bankRateAnnual: 6.5,
          bankTermYears: 10,
          sellerAmount: 500000,
          sellerRateAnnual: 8.0,
          sellerStructure: 'amort',
          sellerAmortizationYears: 5,
          sellerBalloonYears: 5,
          sellerBalloonDueMonth: 12,
          sellerIoMonths: undefined,
          annualSde: 300000,
          monthlySde: 25000,
          financialMetricType: 'EBITDA',
          annualThreshold: 325000,
          quarterlyThreshold: 81250,
          cloneFromId: null
        });
      }
      setErrors({});
    }
  }, [isOpen, editingScenario, cloningScenario]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Scenario name is required';
    }

    if (formData.headlinePrice <= 0) {
      newErrors.headlinePrice = 'Headline price must be greater than 0';
    }

    if (formData.bankAmount <= 0) {
      newErrors.bankAmount = 'Bank amount must be greater than 0';
    }

    if (formData.bankRateAnnual < 0) {
      newErrors.bankRateAnnual = 'Bank rate cannot be negative';
    }

    if (formData.bankTermYears <= 0) {
      newErrors.bankTermYears = 'Bank term must be greater than 0';
    }

    if (formData.sellerAmount <= 0) {
      newErrors.sellerAmount = 'Seller amount must be greater than 0';
    }

    if (formData.sellerRateAnnual < 0) {
      newErrors.sellerRateAnnual = 'Seller rate cannot be negative';
    }

    if (formData.sellerBalloonYears <= 0) {
      newErrors.sellerBalloonYears = 'Seller balloon years must be greater than 0';
    }

    if (formData.sellerBalloonDueMonth <= 0 || formData.sellerBalloonDueMonth > 12) {
      newErrors.sellerBalloonDueMonth = 'Seller balloon due month must be between 1 and 12';
    }

    if (formData.sellerStructure === 'amort' && (!formData.sellerAmortizationYears || formData.sellerAmortizationYears <= 0)) {
      newErrors.sellerAmortizationYears = 'Amortization years is required for amort structure';
    }

    if (formData.sellerStructure === 'io' && (!formData.sellerIoMonths || formData.sellerIoMonths <= 0)) {
      newErrors.sellerIoMonths = 'IO months is required for IO structure';
    }

    if (formData.sellerStructure === 'io_then_amort') {
      if (!formData.sellerIoMonths || formData.sellerIoMonths <= 0) {
        newErrors.sellerIoMonths = 'IO months is required for IO then amort structure';
      }
      if (!formData.sellerAmortizationYears || formData.sellerAmortizationYears <= 0) {
        newErrors.sellerAmortizationYears = 'Amortization years is required for IO then amort structure';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    if (editingScenario && onUpdate) {
      onUpdate(editingScenario.id, formData);
    } else if (cloningScenario && onClone) {
      onClone(cloningScenario.id, formData);
    } else {
      onCreate(formData);
    }
  };

  const handleInputChange = (field: keyof ScenarioFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-lg">
        <h3 className="text-xl font-semibold mb-6">
          {editingScenario ? 'Edit Deal Scenario' : 
           cloningScenario ? 'Clone Deal Scenario' : 
           'Create Deal Scenario'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scenario Name *</label>
              <input
                className={`w-full px-3 py-2 border rounded-md ${errors.name ? 'border-red-500' : ''}`}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Base Case, Optimistic, Conservative"
                autoFocus
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
              <input
                className="w-full px-3 py-2 border rounded-md"
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                placeholder="Business name"
              />
            </div>
          </div>

          {/* Financial Metrics */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium mb-4">Financial Metrics</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Headline Price *</label>
                <input
                  type="number"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md ${errors.headlinePrice ? 'border-red-500' : ''}`}
                  value={formData.headlinePrice}
                  onChange={(e) => handleInputChange('headlinePrice', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
                {errors.headlinePrice && <p className="text-red-500 text-xs mt-1">{errors.headlinePrice}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual SDE</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.annualSde}
                  onChange={(e) => handleInputChange('annualSde', parseFloat(e.target.value) || 0)}
                  placeholder="300000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly SDE</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.monthlySde}
                  onChange={(e) => handleInputChange('monthlySde', parseFloat(e.target.value) || 0)}
                  placeholder="25000"
                />
              </div>
            </div>
          </div>

          {/* Bank Financing */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium mb-4">Bank Financing</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md ${errors.bankAmount ? 'border-red-500' : ''}`}
                  value={formData.bankAmount}
                  onChange={(e) => handleInputChange('bankAmount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
                {errors.bankAmount && <p className="text-red-500 text-xs mt-1">{errors.bankAmount}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Rate (Annual %) *</label>
                <input
                  type="number"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md ${errors.bankRateAnnual ? 'border-red-500' : ''}`}
                  value={formData.bankRateAnnual}
                  onChange={(e) => handleInputChange('bankRateAnnual', parseFloat(e.target.value) || 0)}
                  placeholder="6.50"
                />
                {errors.bankRateAnnual && <p className="text-red-500 text-xs mt-1">{errors.bankRateAnnual}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Term (Years) *</label>
                <input
                  type="number"
                  className={`w-full px-3 py-2 border rounded-md ${errors.bankTermYears ? 'border-red-500' : ''}`}
                  value={formData.bankTermYears}
                  onChange={(e) => handleInputChange('bankTermYears', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
                {errors.bankTermYears && <p className="text-red-500 text-xs mt-1">{errors.bankTermYears}</p>}
              </div>
            </div>
          </div>

          {/* Seller Financing */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium mb-4">Seller Financing</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seller Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md ${errors.sellerAmount ? 'border-red-500' : ''}`}
                  value={formData.sellerAmount}
                  onChange={(e) => handleInputChange('sellerAmount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
                {errors.sellerAmount && <p className="text-red-500 text-xs mt-1">{errors.sellerAmount}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seller Rate (Annual %) *</label>
                <input
                  type="number"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md ${errors.sellerRateAnnual ? 'border-red-500' : ''}`}
                  value={formData.sellerRateAnnual}
                  onChange={(e) => handleInputChange('sellerRateAnnual', parseFloat(e.target.value) || 0)}
                  placeholder="8.00"
                />
                {errors.sellerRateAnnual && <p className="text-red-500 text-xs mt-1">{errors.sellerRateAnnual}</p>}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seller Structure *</label>
                <select
                  className="w-full px-3 py-2 border rounded-md bg-white"
                  value={formData.sellerStructure}
                  onChange={(e) => handleInputChange('sellerStructure', e.target.value as 'amort' | 'io' | 'io_then_amort')}
                >
                  <option value="amort">Amortizing</option>
                  <option value="io">Interest Only</option>
                  <option value="io_then_amort">IO then Amortizing</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Balloon Years *</label>
                <input
                  type="number"
                  className={`w-full px-3 py-2 border rounded-md ${errors.sellerBalloonYears ? 'border-red-500' : ''}`}
                  value={formData.sellerBalloonYears}
                  onChange={(e) => handleInputChange('sellerBalloonYears', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
                {errors.sellerBalloonYears && <p className="text-red-500 text-xs mt-1">{errors.sellerBalloonYears}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Balloon Due Month *</label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  className={`w-full px-3 py-2 border rounded-md ${errors.sellerBalloonDueMonth ? 'border-red-500' : ''}`}
                  value={formData.sellerBalloonDueMonth}
                  onChange={(e) => handleInputChange('sellerBalloonDueMonth', parseInt(e.target.value) || 0)}
                  placeholder="1-12"
                />
                {errors.sellerBalloonDueMonth && <p className="text-red-500 text-xs mt-1">{errors.sellerBalloonDueMonth}</p>}
              </div>
              
              {(formData.sellerStructure === 'amort' || formData.sellerStructure === 'io_then_amort') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amortization Years *</label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 border rounded-md ${errors.sellerAmortizationYears ? 'border-red-500' : ''}`}
                    value={formData.sellerAmortizationYears || ''}
                    onChange={(e) => handleInputChange('sellerAmortizationYears', parseInt(e.target.value) || undefined)}
                    placeholder="0"
                  />
                  {errors.sellerAmortizationYears && <p className="text-red-500 text-xs mt-1">{errors.sellerAmortizationYears}</p>}
                </div>
              )}
              
              {(formData.sellerStructure === 'io' || formData.sellerStructure === 'io_then_amort') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IO Months *</label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 border rounded-md ${errors.sellerIoMonths ? 'border-red-500' : ''}`}
                    value={formData.sellerIoMonths || ''}
                    onChange={(e) => handleInputChange('sellerIoMonths', parseInt(e.target.value) || undefined)}
                    placeholder="0"
                  />
                  {errors.sellerIoMonths && <p className="text-red-500 text-xs mt-1">{errors.sellerIoMonths}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Performance Thresholds */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium mb-4">Performance Thresholds</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Financial Metric Type</label>
                <input
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.financialMetricType}
                  onChange={(e) => handleInputChange('financialMetricType', e.target.value)}
                  placeholder="EBITDA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual Threshold</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.annualThreshold}
                  onChange={(e) => handleInputChange('annualThreshold', parseFloat(e.target.value) || 0)}
                  placeholder="325000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quarterly Threshold</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.quarterlyThreshold}
                  onChange={(e) => handleInputChange('quarterlyThreshold', parseFloat(e.target.value) || 0)}
                  placeholder="81250"
                />
              </div>
            </div>
          </div>

          {/* Clone Option */}
          {existingScenarios.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-lg font-medium mb-4">Clone from Existing Scenario (Optional)</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clone from</label>
                <select
                  className="w-full px-3 py-2 border rounded-md bg-white"
                  value={formData.cloneFromId || ''}
                  onChange={(e) => handleInputChange('cloneFromId', e.target.value || null)}
                >
                  <option value="">Start from blank</option>
                  {existingScenarios.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-6 border-t">
            <button type="button" className="px-6 py-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200" onClick={onClose}>Cancel</button>
            <button type="submit" className="px-6 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700">
              {editingScenario ? 'Update Scenario' : 
               cloningScenario ? 'Clone Scenario' : 
               'Create Scenario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



